import React, { useContext, useCallback, useEffect, useState, useMemo } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import produce from 'immer';
import { modeUIContext, DialogResponse, DialogResponseCode, DialogType, showDialog, showErrorNotification } from '@moderepo/mode-ui-state';
import { LogRequestStatus, LogRequestData } from '@moderepo/mode-internal-apis';
import { fetchDeviceLogRequestEntries, cancelDeviceLogRequest, createDeviceLogRequest, selectProjectById, selectDeviceLogRequests, ritzDataContext }
    from '@moderepo/mode-data-state-ritz';
import { PaginationBar, ITEMS_PER_PAGE_10, capitalizeFirst, capitalizeAll, ITEMS_PER_PAGE_LIST, PAGING_MIN_PAGES, useSearchParams,
    BaseListCompField, GenericMessageBox } from '@moderepo/mode-app-components';
import { LogsLookup, LogRequestsTable, LogRequestsTableProps } from '@moderepo/mode-app-components-internal';
import { SortOrder } from '@moderepo/mode-apis';
import { useRitzTranslation } from '../../utils/customHooks';
import { localizeLogRequestEntries, sortLogRequestEntries } from '../containerUtils';
import { CreateLogRequestDialog } from '../../components';


/**
 * Define the set of fields that the user can sort by
 */
enum SortField {
    TIMESTAMP = 'ts',
    STATUS = 'status',
}


interface LogRequestStatusOption {
    readonly name: string;
    readonly value: string;
}


/**
 * Define the set of filter options for the request statuses. This will be used for building the filter by statuses
 * dropdown
 */
const LogRequestStatusFilterOptions: {[key: string]: LogRequestStatusOption} = Object.freeze({
    ALL: {
        name : 'filter.log_requests.all',
        value: Object.values(LogRequestStatus).join(','),
    },
    REQUESTED: {
        name : 'filter.log_requests.requested',
        value: LogRequestStatus.REQUESTED,
    },
    UPLOADING: {
        name : 'filter.log_requests.uploading',
        value: LogRequestStatus.UPLOADING,
    },
    CANCELLED: {
        name : 'filter.log_requests.cancelled',
        value: LogRequestStatus.CANCELLED,
    },
    SUCCESS: {
        name : 'filter.log_requests.success',
        value: LogRequestStatus.SUCCESS,
    },
    FAILURE: {
        name : 'filter.log_requests.failure',
        value: LogRequestStatus.FAILURE,
    },
    NOLOGS: {
        name : 'filter.log_requests.no_logs',
        value: LogRequestStatus.NOLOGS,
    },
});



/**
 * Custom search params. For log request, we will need these additional search params, sortField, sortOrder, and statuses
 */
interface LogRequestSearchParams {
    readonly sortField: SortField;
    readonly sortOrder: SortOrder;
    readonly statuses: LogRequestStatusOption;
}



/**
 * Because this is a container, not a page, it doesn't and shouldn't have access to the route params. Therefore, the page
 * that display this container must provide these information.
 */
interface DeviceLogRequestContainerProps {
    readonly projectId: number;
    readonly deviceId: number;
}


/**
 * This is the Container that display a device's message logs. It is a SMART component which contains all the logics but
 * no UI.
 */
export const DeviceLogRequestsContainer: React.FC<DeviceLogRequestContainerProps> = (props: DeviceLogRequestContainerProps) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const [showCreateLogRequestDialog, setShowCreateLogRequestDialog] = useState<boolean>(false);


    const {
        searchParams, onOtherFieldsChange, onItemsPerPageChange, onPrevPage, onNextPage, onPage,
    } = useSearchParams<LogRequestSearchParams>({
        searchText : '',
        otherFields: {
            sortField: SortField.TIMESTAMP,
            sortOrder: SortOrder.DESC,
            statuses : LogRequestStatusFilterOptions.ALL,
        },
        itemsPerPage: ITEMS_PER_PAGE_10,
        currentPage : 0,
    });


    // This is the complete set of log entries that we have requested
    const logEntries = selectDeviceLogRequests(dataState, props.deviceId, {
        sortBy    : `${searchParams.otherFields.sortField}:${searchParams.otherFields.sortOrder}`,
        statuses  : searchParams.otherFields.statuses.value,
        pageNumber: searchParams.currentPage,
        pageSize  : searchParams.itemsPerPage,
    });

    // sort the log entries by the current sort field and order
    const sortedLogEntries = useMemo(() => {
        return sortLogRequestEntries(logEntries, searchParams.otherFields.sortField, searchParams.otherFields.sortOrder);
    }, [logEntries, searchParams.otherFields.sortField, searchParams.otherFields.sortOrder]);


    // Pre-translated the log entries data before we pass it to the LogRequestTableComp because MessageLogsComponents
    // does not know how to localize the data.
    const translatedLogEntries = useMemo(() => {
        return localizeLogRequestEntries(sortedLogEntries ? sortedLogEntries.items : undefined, trans);
    }, [sortedLogEntries, trans]);


    /**
     * Load log entries base on the filters from the current searchParams
     */
    const loadLogEntries = useCallback(async (deviceId: number) => {
        if (searchParams && searchParams.otherFields) {
            try {
                dataDispatch(fetchDeviceLogRequestEntries(
                    deviceId,
                    `${searchParams.otherFields.sortField}:${searchParams.otherFields.sortOrder}`,
                    searchParams.otherFields.statuses.value,
                    searchParams.currentPage,
                    searchParams.itemsPerPage,
                )).catch(() => { /* ignore error */ });
            } catch (error) {
            /* ignore error */
            }
        }
    }, [dataDispatch, searchParams]);



    useEffect(() => {
        if (searchParams.otherFields) {
            loadLogEntries(props.deviceId);
        }
    }, [loadLogEntries, props.deviceId, searchParams]);


    /**
     * On Cancel log request button clicked. Confirm with the user before cancelling the request
     */
    const onCancelLogRequest = useCallback((logRequest: LogRequestData) => {
        uiDispatch(showDialog({
            type          : DialogType.CONFIRM,
            title         : 'dialog.confirm_cancel_log_request.title',
            message       : 'dialog.confirm_cancel_log_request.message',
            messageData   : { request_id: logRequest.id },
            positiveButton: 'label.yes',
            negativeButton: 'label.no',
            onClose       : async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    dataDispatch(cancelDeviceLogRequest(props.deviceId, logRequest.id)).then(() => {
                        loadLogEntries(props.deviceId);
                    }).catch(() => {
                        /* ignore error */
                    });
                }
            },

        }));
    }, [dataDispatch, uiDispatch, loadLogEntries, props.deviceId]);


    /**
     * On create request button click. We will display a dialog for the user to input start, end, and prefixes.
     * Also, we will not allow the user to create new request if there is already a request in pending.
     */
    const onCreateRequest = useCallback(() => {
        if (logEntries && logEntries.items) {
            // Check if there is a pending log request. If there is, show an error message.
            if (logEntries.items.find((item: LogRequestData): boolean => {
                return item.status === LogRequestStatus.REQUESTED;
            })) {
                uiDispatch(showErrorNotification('error.message.blocking_log_request'));
            } else {
                setShowCreateLogRequestDialog(true);
            }
        } else {
            setShowCreateLogRequestDialog(true);
        }
    }, [uiDispatch, logEntries, setShowCreateLogRequestDialog]);



    /**
     * Callback when the user is done providing start, end, and prefixes values. We will make an API call to
     * create a log request.
     */
    const onSubmitCreateRequest = useCallback(async (startDate: Date | undefined, endDate: Date | undefined, prefixes: string[] | undefined) => {
        setShowCreateLogRequestDialog(false);
        dataDispatch(createDeviceLogRequest(
            props.deviceId, startDate ? startDate.toISOString() : undefined, endDate ? endDate.toISOString() : undefined, prefixes,
        )).then(() => {
            loadLogEntries(props.deviceId);
        }).catch(() => {
            /* ignore error */
        });
    }, [dataDispatch, loadLogEntries, props.deviceId]);


    /**
     * On filter selected
     */
    const onFilterSelected = useCallback((filterName: string, selectedValue: LogRequestStatusOption) => {
        if (searchParams.otherFields) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: any) => {
                draft.statuses = selectedValue;
            }));
        }
    }, [searchParams, onOtherFieldsChange]);


    const onActionClicked = useCallback(() => {
        onCreateRequest();
    }, [onCreateRequest]);



    /**
     * On download log file clicked.
     */
    const onDownloadLogRequestFile = useCallback((logRequest: LogRequestData) => {
        window.open(logRequest.url, 'blank');
    }, []);



    /**
     * On column clicked, change the sort order/field
     */
    const onSortByField = useCallback((field: BaseListCompField) => {
        if (searchParams.otherFields) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: any) => {

                if (draft.sortField === field.dataItemProp) {
                    // this column is currently being sorted, change the toggle the sort order instead
                    if (draft.sortOrder === SortOrder.ASC) {
                        draft.sortOrder = SortOrder.DESC;
                    } else {
                        draft.sortOrder = SortOrder.ASC;
                    }
                } else {
                    // This column is not the current column being sorted, change it to this column and
                    draft.sortField = field.dataItemProp;
                }
            }));
        }
    }, [searchParams, onOtherFieldsChange]);


    return (
        <>
            {project && (
                <>
                    {searchParams && searchParams.otherFields && (
                        <LogsLookup<LogRequestsTableProps>
                            filters={{
                                filters: [{
                                    name               : 'agent',
                                    label              : capitalizeAll(trans('label.status')),
                                    selectAnOptionLabel: capitalizeFirst(trans('label.select_an_agent')),
                                    selectedOption     : searchParams.otherFields.statuses ? {
                                        id   : searchParams.otherFields.statuses.value,
                                        label: capitalizeFirst(trans(searchParams.otherFields.statuses.name)),
                                        value: searchParams.otherFields.statuses,
                                    } : undefined,
                                    options: Object.values(LogRequestStatusFilterOptions).map((option: {name: string, value: string}) => {
                                        return {
                                            id   : option.value,
                                            label: capitalizeFirst(trans(option.name)),
                                            value: option,
                                        };
                                    }),
                                }],
                                onFilterSelected,
                            }}

                            logEntriesLoaded={logEntries !== undefined}
                            hasSearchResult={logEntries !== undefined && logEntries.items.length > 0}
                            logDisplayComp={LogRequestsTable}
                            logDisplayCompProps={{
                                customPrimaryActions: [{
                                    icon   : 'add',
                                    variant: 'contained',
                                    color  : 'primary',
                                    label  : capitalizeFirst(trans('label.create_log_request')),
                                    onClick: onActionClicked,
                                }],

                                texts: {
                                    cancelRequestTooltip      : capitalizeFirst(trans('label.cancel_log_request')),
                                    downloadLogTooltip        : capitalizeFirst(trans('label.download_log_file')),
                                    reloadRequestStatusTooltip: capitalizeFirst(trans('label.reload_log_requests')),
                                },

                                fieldsSettings: {
                                    fields: {
                                        timestamp: {
                                            dataItemProp: 'ts',
                                            label       : capitalizeAll(trans('label.timestamp')),
                                            sortable    : logEntries && logEntries.items.length > 1,
                                        },
                                        startDate: {
                                            dataItemProp: 'startDate',
                                            label       : capitalizeAll(trans('label.start_date')),
                                        },
                                        endDate: {
                                            dataItemProp: 'endDate',
                                            label       : capitalizeAll(trans('label.end_date')),
                                        },
                                        prefixes: {
                                            dataItemProp: 'prefixes',
                                            label       : capitalizeAll(trans('label.prefixes')),
                                        },
                                        status: {
                                            dataItemProp: 'status',
                                            label       : capitalizeAll(trans('label.status')),
                                            sortable    : logEntries && logEntries.items.length > 1,
                                        },
                                        action: {
                                            label: '',
                                        },
                                    },

                                    onSortByField,
                                    currentSortedField: searchParams.otherFields ? searchParams.otherFields.sortField : undefined,
                                    currentSortedOrder: searchParams.otherFields ? searchParams.otherFields.sortOrder : undefined,
                                },

                                listData             : translatedLogEntries || [],
                                onCancelLogRequest,
                                onDownloadLogRequestFile,
                                onReloadRequestStatus: () => {
                                    loadLogEntries(props.deviceId);
                                },
                            }}
                            paginationComp={(
                                <PaginationBar {...{
                                    itemsPerPageList: ITEMS_PER_PAGE_LIST,
                                    itemsPerPage    : searchParams.itemsPerPage,
                                    pagingMinPages  : PAGING_MIN_PAGES,
                                    currentPage     : searchParams.currentPage,
                                    totalItems      : logEntries && logEntries.range ? logEntries.range.total : undefined,
                                    hasMoreItems    : logEntries !== undefined && (logEntries.range
                                        ? logEntries.range.total > searchParams.itemsPerPage * (searchParams.currentPage + 1)
                                        : (logEntries.items.length >= searchParams.itemsPerPage)),
                                    texts: {
                                        itemsPerPage: capitalizeAll(trans('label.paging.items_per_page', {
                                            item_name: trans('label.entry', { count: 2 }),
                                        })),
                                        pageNTemplate   : capitalizeAll(trans('label.page_n')),
                                        pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                                    },
                                    onItemsPerPageChange,
                                    onNextPage,
                                    onPrevPage,
                                    onPage,
                                }}
                                />
                            )}
                            noDataMessageComp={(
                                <GenericMessageBox
                                    title={trans('generic.no_data.title', {
                                        term: capitalizeAll(trans('label.logs', { count: 2 })),
                                    })}
                                />
                            )}
                            emptyDataMessageComp={(
                                <GenericMessageBox
                                    title={trans('generic.no_data.title', {
                                        term: capitalizeAll(trans('label.log_entry', { count: 2 })),
                                    })}
                                />
                            )}
                        />
                    )}
                </>
            )}

            {(!project) && (
                <CircularProgress />
            )}


            <CreateLogRequestDialog
                projectId={props.projectId}
                open={showCreateLogRequestDialog}
                onCancel={() => {
                    setShowCreateLogRequestDialog(false);
                }}
                onCreateRequest={onSubmitCreateRequest}
            />
        </>
    );
};
